import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDT, today, confirmDelete } from "@/lib/api";
import { PageHeader, Sel, Tabs, CallBadge, Kpi, Section, Empty } from "@/components/common";
import { CallDialog } from "@/components/ActionDialogs";

const CallsTab = ({ uid, date, reload }) => {
  const { meta, isAdmin } = useAuth();
  const [status, setStatus] = useState("");
  const [stats, setStats] = useState(null);
  const [calls, setCalls] = useState([]);
  useEffect(() => {
    api.get("/calls/daily-stats", { params: { date } }).then((r) => setStats(r.data));
    api.get("/calls", { params: { date_from: date, date_to: date, user_id: uid || undefined, status: status || undefined } }).then((r) => setCalls(r.data));
  }, [uid, date, status, reload]);
  if (!stats) return null;
  const s = uid ? stats.by_user.find((x) => x.user_id === uid) || stats.summary : stats.summary;
  return (
    <>
      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7" data-testid="call-daily-stats">
        <Kpi testid="calls-stat-total" label="Total calls" value={s.total} /><Kpi testid="calls-stat-connected" label="Connected" value={s.connected} tone="green" />
        <Kpi testid="calls-stat-no-answer" label="No answer" value={s.no_answer} /><Kpi testid="calls-stat-busy" label="Busy" value={s.busy} />
        <Kpi testid="calls-stat-followups" label="Follow-ups generated" value={s.followups} /><Kpi testid="calls-stat-qualified" label="Qualified" value={s.qualified} />
        <Kpi testid="calls-stat-disqualified" label="Disqualified" value={s.disqualified} />
      </div>
      {isAdmin && !uid && (
        <Section title="Calling activity by salesperson" className="mb-5">
          <div className="overflow-x-auto"><table className="crm-table" data-testid="calls-by-user-table">
            <thead><tr>{["Salesperson", "Total", "Connected", "No answer", "Busy", "Follow-ups", "Qualified", "Disqualified", "Connect rate"].map((h) => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>{stats.by_user.map((u) => <tr key={u.user_id}><td className="font-semibold">{u.name}</td>{["total", "connected", "no_answer", "busy", "followups", "qualified", "disqualified"].map((k) => <td key={k} className="num">{u[k]}</td>)}<td className="num">{u.total ? Math.round((u.connected / u.total) * 100) : 0}%</td></tr>)}</tbody>
          </table></div>
        </Section>
      )}
      <Section title="Call log" action={<div className="w-44"><Sel testid="calls-filter-status" value={status} onChange={setStatus} placeholder="All statuses" options={meta.call_statuses} /></div>}>
        <div className="overflow-x-auto"><table className="crm-table" data-testid="calls-table">
          <thead><tr>{["Time", "Client", "Status", "Duration", "Notes", "Next follow-up", "Salesperson", ...(isAdmin ? [""] : [])].map((h) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>{calls.map((c) => (
            <tr key={c.id}><td className="num text-xs">{fmtDT(c.called_at)}</td><td><Link to={`/leads/${c.lead_id}`} className="font-semibold hover:text-primary">{c.client}</Link></td>
              <td><CallBadge s={c.status} /></td><td className="num text-xs">{c.duration_min || 0}m</td><td className="max-w-72 truncate text-xs">{c.notes || "—"}</td>
              <td className="text-xs">{fmtDT(c.next_follow_up)}</td><td className="text-xs">{c.user_name}</td>{isAdmin && <td><button data-testid={`call-delete-${c.id}`} onClick={() => confirmDelete(`/calls/${c.id}`, "call", () => setCalls((x) => x.filter((y) => y.id !== c.id)))} className="text-xs font-bold text-rose-600 hover:underline">Delete</button></td>}</tr>
          ))}</tbody>
        </table>{!calls.length && <Empty text="No calls on this date" />}</div>
      </Section>
    </>
  );
};

const FeedTab = ({ uid, date, audit }) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/activities", { params: { user_id: uid || undefined, date_from: audit ? undefined : date, date_to: audit ? undefined : date, audit } }).then((r) => setItems(r.data));
  }, [uid, date, audit]);
  return (
    <div className="crm-card overflow-x-auto">
      <table className="crm-table" data-testid={audit ? "audit-table" : "activity-feed-table"}>
        <thead><tr>{["Date & time", "Who", "Action", "Client", "Details"].map((h) => <th key={h}>{h}</th>)}</tr></thead>
        <tbody>{items.map((a) => (
          <tr key={a.id}><td className="num text-xs">{fmtDT(a.created_at)}</td><td className="text-xs font-semibold">{a.actor_name}</td>
            <td><span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold">{a.type.replace(/_/g, " ")}</span></td>
            <td>{a.lead_id ? <Link to={`/leads/${a.lead_id}`} className="font-semibold hover:text-primary">{a.client}</Link> : "—"}</td>
            <td className="max-w-md whitespace-normal text-xs">{a.message}{a.meta?.changes && <span className="block text-muted-foreground">{Object.entries(a.meta.changes).map(([k, v]) => `${k}: ${JSON.stringify(v.from)} → ${JSON.stringify(v.to)}`).join(" · ")}</span>}</td></tr>
        ))}</tbody>
      </table>
      {!items.length && <Empty text="No activity" />}
    </div>
  );
};

export default function Activities() {
  const { isAdmin, salesUsers } = useAuth();
  const [sp] = useSearchParams();
  const [tab, setTab] = useState("calls");
  const [uid, setUid] = useState(sp.get("user") || "");
  const [date, setDate] = useState(today());
  const [open, setOpen] = useState(false);
  const [reload, setReload] = useState(0);
  const done = useCallback(() => setReload((x) => x + 1), []);
  return (
    <div>
      <PageHeader title="Activities" subtitle="Calls, sales activity and audit trail">
        <input type="date" data-testid="activities-date" className="crm-input w-40" value={date} onChange={(e) => setDate(e.target.value)} />
        {isAdmin && <div className="w-44"><Sel testid="activities-filter-salesperson" value={uid} onChange={setUid} placeholder="All salespeople" options={salesUsers.map((u) => ({ value: u.id, label: u.name }))} /></div>}
        <button data-testid="log-call-button" onClick={() => setOpen(true)} className="flex h-9 items-center gap-1.5 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:opacity-90"><Plus className="h-4 w-4" /> Log call</button>
      </PageHeader>
      <Tabs testid="activities-tabs" value={tab} onChange={setTab} tabs={[{ value: "calls", label: "Calls" }, { value: "feed", label: "Activity feed" }, ...(isAdmin ? [{ value: "audit", label: "Audit trail" }] : [])]} />
      {tab === "calls" && <CallsTab uid={uid} date={date} reload={reload} />}
      {tab === "feed" && <FeedTab uid={uid} date={date} />}
      {tab === "audit" && <FeedTab uid={uid} date={date} audit />}
      <CallDialog open={open} onOpenChange={setOpen} onDone={done} />
    </div>
  );
}
